<template>
  <div
    ref="captchaContainer"
    class="hidden"
  />
  <slot />
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import type { UseRecaptchaComposable } from './useRecaptcha';
import { useCheckoutStore } from '@/stores/checkout';

declare var grecaptcha: unknown | undefined;

const captchaContainer = ref<HTMLDivElement>();

const checkoutStore = useCheckoutStore();

const initialized = ref<boolean | 'loading'>(false);
const enabled = computed(() => checkoutStore.recaptchaEnabled);

const initialize = async () => {
  if (initialized.value == true) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (!enabled.value) {
      resolve('');
      return;
    }

    if (!captchaContainer.value) {
      reject('No captchaContainer found.');
      return;
    }

    initialized.value = 'loading';

    const captchaScriptEl = document.createElement('script');
    captchaScriptEl.src = `https://www.google.com/recaptcha/api.js?render=${checkoutStore.reCaptchaSiteKey}`;
    captchaScriptEl.type = 'text/javascript';
    captchaScriptEl.defer = true;
    captchaScriptEl.async = true;
    captchaScriptEl.onload = () => {
      initialized.value = true;
      resolve('');
    };

    captchaContainer.value.appendChild(captchaScriptEl);
  });
};

const execute = async (action: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (typeof grecaptcha === 'undefined' || !grecaptcha) {
      reject('Recaptcha is not initialized');
    }

    grecaptcha.ready(async () => {
      grecaptcha.execute(checkoutStore.reCaptchaSiteKey, { action }).then(resolve, reject);
    });
  });
};

const ready = computed(() => initialized.value === true);

const withRecaptchaInitialized = cb => {
  return async (...args) => {
    if (ready.value) {
      return cb(...args);
    }
    await initialize();
    return cb(...args);
  };
};

const executeCheckbox = (action?: string): Promise<string> => {
  return new Promise(resolve => {
    window.onCaptchaCheckboxLoad = () => {
      grecaptcha.enterprise.render('captcha-checkbox-container', {
        sitekey: checkoutStore.recaptchaCheckboxSiteKey,
        action,
        callback: resolve,
      });
    };

    const captchaScriptEl = document.createElement('script');
    captchaScriptEl.src = 'https://www.google.com/recaptcha/enterprise.js?onload=onCaptchaCheckboxLoad&render=explicit';
    captchaScriptEl.type = 'text/javascript';
    captchaScriptEl.defer = true;
    captchaScriptEl.async = true;

    captchaContainer.value.appendChild(captchaScriptEl);
  });
};

provide<UseRecaptchaComposable>('recaptcha', {
  ready,
  init: initialize,
  executeRecaptcha: withRecaptchaInitialized(execute),
  executeCheckbox,
});
</script>
